import { RootState } from 'modules/store';
import { ErrorObject } from 'types/global';
import Errors from 'modules/shared/error/errors';
import { createAsyncThunk } from '@reduxjs/toolkit';
import Message from 'view/shared/message';
import AccountsService from './accounts.service';
import {
  AccountsError,
  AccountsResponse,
  SettlementScheduleError,
  SettlementScheduleResponse,
  SettlementSummariesError,
  SettlementSummariesResponse,
} from './accounts.types';

export const getAccountsAction = createAsyncThunk<
  AccountsResponse[],
  undefined,
  {
    state: RootState;
    rejectValue: AccountsError | ErrorObject;
  }
>('accounts/get-all-accounts', async (_, { getState, rejectWithValue }) => {
  const { environment: appEnvironment, appToken } = getState().root.global;

  try {
    return await AccountsService.getAllAccounts({
      appToken,
      appEnvironment,
    });
  } catch (error: unknown) {
    const errorObj: ErrorObject = error as ErrorObject;

    if (Errors.errorStatusOrStatusCode(errorObj) === 'DATA_NOT_FOUND') {
      Message.error('Accounts data not found.');
      return rejectWithValue(errorObj);
    }

    Errors.handle(errorObj);
    return rejectWithValue(errorObj);
  }
});

export const getSettlementScheduleAction = createAsyncThunk<
  SettlementScheduleResponse,
  undefined,
  {
    state: RootState;
    rejectValue: SettlementScheduleError | ErrorObject;
  }
>(
  'accounts/get-settlement-schedule',
  async (_, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      return await AccountsService.getSettlementSchedule({
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      const errorObj: ErrorObject = error as ErrorObject;

      if (Errors.errorStatusOrStatusCode(errorObj) === 'DATA_NOT_FOUND') {
        Message.error('Settlement schedule data not found.');
        return rejectWithValue(errorObj);
      }

      Errors.handle(errorObj);
      return rejectWithValue(errorObj);
    }
  },
);

export const getSettlementSummariesAction = createAsyncThunk<
  SettlementSummariesResponse,
  { page: number; pageSize: number },
  {
    state: RootState;
    rejectValue: SettlementSummariesError | ErrorObject;
  }
>(
  'accounts/get-settlement-summaries',
  async ({ page, pageSize }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      return await AccountsService.getSettlementSummaries({
        page,
        pageSize,
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      const errorObj: ErrorObject = error as ErrorObject;

      if (Errors.errorStatusOrStatusCode(errorObj) === 'DATA_NOT_FOUND') {
        Message.error('Settlement summaries data not found.');
        return rejectWithValue(errorObj);
      }

      Errors.handle(errorObj);
      return rejectWithValue(errorObj);
    }
  },
);
